/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as moment from 'moment/moment';
import {finalize} from 'rxjs/operators';

import {EmpTreeService} from '../../../+employees/emp-tree/emp-tree.service';
import {ApiService} from '../../api/api.service';
import {ModalService} from '../../modal/modal.service';
import {DateLocalizationPipe} from '../../pipes/date-localization.pipe';
import {isPresent} from '../../utils/lang';
import {getInitials, isValidPhotoUrl} from '../../utils/strings';

import {AbstractField} from './abstract-field';

/**
 * An tree widget to select a value from a tree
 */
@Component({
  selector: 'gp-form-field-people-tree',
  template: `
    <ng-select class="form-control"
               ngDefaultControl
               [(ngModel)]="value.value"
               [formControl]="control"
               [attr.test-id]="descriptor.zoneID"
               [items]="data"
               [idField]="'value'"
               [textField]="'display'"
               [readOnly]="readOnly"
               [active]="activeValue"
               (selected)="select($event)">
    </ng-select>
  `,
  providers: [
    DateLocalizationPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PeopleTreeComponent extends AbstractField implements OnInit {
  public data: any[] = [];
  public activeValue: number;
  public previousValue: number;

  constructor(private changeDetectorRef: ChangeDetectorRef,
              private empTreeService: EmpTreeService,
              private modalService: ModalService,
              private translate: TranslateService,
              private dateLocalizationPipe: DateLocalizationPipe,
              private api: ApiService) {
    super();
  }

  ngOnInit(): void {
    this.empTreeService.getPeopleManageNodes(this.descriptor?.metadatas?.rel)
      .pipe(
        finalize(() => {
          this.activeValue = this.value?.value;
          this.previousValue = this.activeValue;
          this.changeDetectorRef.markForCheck();
        })
      )
      .subscribe((response: any[]) => {
        this.data = response
          .filter((element: any) =>
            (!this.groupZoneDescriptor.metadatas || element.cliEmpId !== this.groupZoneDescriptor.metadatas.employeeId))
          .map((element) => {
            const nameParts = (element.nodeName || '').trim().split(/\s+/);
            const initials = getInitials(nameParts[0], nameParts.length > 1 ? nameParts[nameParts.length - 1] : '');
            const avatar = isValidPhotoUrl(element.photo?.href)
              ? `<span class="circle avatar avatar-text w-32">
                    <img src="${this.api.apiHost + element.photo.href}" alt="">
                </span>`
              : `<span class="gp-initials-avatar gp-avatar-sm">${initials}</span>`;
            const display = `<div class="emp">
                                <div class="emp-avatar">
                                    ${avatar}
                                </div>
                                <div class="emp-info">
                                    <div>${element.nodeName}</div>
                                    <small class="text-muted">(${
              this.dateLocalizationPipe.transform(element.beginDate)
            } - ${
              this.dateLocalizationPipe.transform(element.endDate)
            })
                                    </small>
                                </div>
                            </div>`;

            return {
              value: element.id,
              display
            };
          });
        if (!this.descriptor.metadatas) {
          this.descriptor.metadatas = {};
        }
        this.descriptor.metadatas.items = response;
      });
  }

  public select(selectEmployee: any) {
    const item = this.findItem(selectEmployee.id);

    this.value.transId = item.nodeName;

    if (isPresent(item)) {
      if (this.groupZoneDescriptor.isDated) {
        const beginDate = this.control.parent.controls['begindate'].value;
        const endDate = this.control.parent.controls['enddate'].value;

        if (isPresent(item)
          && (moment(item.beginDate).isAfter(beginDate)
            || moment(item.endDate).isBefore(endDate))) {
          this.modalService.alert({
            title: this.translate.instant('COMMON.alert'),
            text: this.translate.instant('DIALOG.assign_manager_only_from', {
              name: `${item.nodeName}`,
              begin: this.dateLocalizationPipe.transform(item.beginDate),
              end: this.dateLocalizationPipe.transform(item.endDate),
              description: item.description || ''
            })
          }).then(() => {
            this.value.value = this.previousValue;
          });
        }
      }
    } else {
      this.previousValue = selectEmployee.id;
    }

    this.changeDetectorRef.markForCheck();
  }

  public findItem(id: number): any {
    return this.descriptor.metadatas.items
      .find(element => element.id === id);
  }
}
